import { CanvasTexture, Color, RepeatWrapping, SRGBColorSpace, Texture } from 'three';

type Painter = (ctx: CanvasRenderingContext2D, size: number) => void;

/**
 * Builds and caches all procedural textures. Everything is drawn to a canvas at
 * runtime — no binary assets and no network needed — which keeps the project
 * self-contained while still looking far richer than flat colours.
 *
 * Single responsibility: turn parameters into ready-to-use repeating textures.
 */
export class TextureLibrary {
  private readonly cache = new Map<string, Texture>();

  asphalt(): Texture {
    return this.cached('asphalt', () => this.build(256, paintAsphalt));
  }

  sidewalk(): Texture {
    return this.cached('sidewalk', () => this.build(256, paintSidewalk));
  }

  grass(): Texture {
    return this.cached('grass', () => this.build(256, paintGrass));
  }

  roof(): Texture {
    return this.cached('roof', () => this.build(256, paintRoof));
  }

  /** A building facade for the given base colour, with lit/unlit windows. */
  facade(baseColor: number): Texture {
    return this.cached(`facade-${baseColor}`, () =>
      this.build(256, (ctx, size) => paintFacade(ctx, size, baseColor)),
    );
  }

  private cached(key: string, factory: () => Texture): Texture {
    let tex = this.cache.get(key);
    if (!tex) {
      tex = factory();
      this.cache.set(key, tex);
    }
    return tex;
  }

  private build(size: number, paint: Painter): Texture {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    paint(ctx, size);
    const texture = new CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.colorSpace = SRGBColorSpace;
    texture.anisotropy = 8;
    return texture;
  }
}

// --- painters (pure functions of a 2D context) ---------------------------

function speckle(ctx: CanvasRenderingContext2D, size: number, count: number, alpha: number): void {
  for (let i = 0; i < count; i++) {
    const shade = Math.random() < 0.5 ? 0 : 255;
    ctx.fillStyle = `rgba(${shade},${shade},${shade},${alpha * Math.random()})`;
    ctx.fillRect(Math.random() * size, Math.random() * size, 1.5, 1.5);
  }
}

function paintAsphalt(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.fillStyle = '#34373f';
  ctx.fillRect(0, 0, size, size);
  speckle(ctx, size, 2600, 0.5);
  // a few faint cracks
  ctx.strokeStyle = 'rgba(15,15,18,0.5)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 6; i++) {
    ctx.beginPath();
    let x = Math.random() * size;
    let y = Math.random() * size;
    ctx.moveTo(x, y);
    for (let s = 0; s < 5; s++) {
      x += (Math.random() - 0.5) * 60;
      y += (Math.random() - 0.5) * 60;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}

function paintSidewalk(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.fillStyle = '#9aa0ad';
  ctx.fillRect(0, 0, size, size);
  speckle(ctx, size, 1800, 0.35);
  // paving-slab grid
  ctx.strokeStyle = 'rgba(70,74,84,0.7)';
  ctx.lineWidth = 3;
  const cells = 4;
  const step = size / cells;
  for (let i = 0; i <= cells; i++) {
    ctx.beginPath();
    ctx.moveTo(i * step, 0);
    ctx.lineTo(i * step, size);
    ctx.moveTo(0, i * step);
    ctx.lineTo(size, i * step);
    ctx.stroke();
  }
}

function paintGrass(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.fillStyle = '#5fae4a';
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 4000; i++) {
    const g = 120 + Math.random() * 90;
    ctx.fillStyle = `rgba(${40 + Math.random() * 30},${g},${50 + Math.random() * 30},0.5)`;
    ctx.fillRect(Math.random() * size, Math.random() * size, 2, 3);
  }
}

function paintRoof(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.fillStyle = '#5a5e66';
  ctx.fillRect(0, 0, size, size);
  speckle(ctx, size, 3000, 0.55);
}

function paintFacade(ctx: CanvasRenderingContext2D, size: number, baseColor: number): void {
  const base = new Color(baseColor);
  ctx.fillStyle = `#${base.getHexString()}`;
  ctx.fillRect(0, 0, size, size);

  // subtle vertical shading for depth
  const grad = ctx.createLinearGradient(0, 0, 0, size);
  grad.addColorStop(0, 'rgba(255,255,255,0.10)');
  grad.addColorStop(1, 'rgba(0,0,0,0.18)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  // window grid with frames and randomly lit panes
  const cols = 4;
  const rows = 4;
  const margin = 10;
  const w = (size - margin * (cols + 1)) / cols;
  const h = (size - margin * (rows + 1)) / rows;
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const x = margin + i * (w + margin);
      const y = margin + j * (h + margin);
      // frame
      ctx.fillStyle = 'rgba(20,26,40,0.85)';
      ctx.fillRect(x - 2, y - 2, w + 4, h + 4);
      // pane
      const lit = Math.random() < 0.45;
      if (lit) {
        ctx.fillStyle = Math.random() < 0.5 ? '#fff3b0' : '#9fe8ff';
      } else {
        ctx.fillStyle = 'rgba(30,40,60,0.92)';
      }
      ctx.fillRect(x, y, w, h);
      // glass highlight
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.fillRect(x, y, w, h * 0.3);
    }
  }
}
